import { ObjectDB, ObjectId } from "@novorender/data-js-api";
import { HierarcicalObjectReference } from "@novorender/data-js-api";
import pMap from "p-map";

import { AsyncTreeChangeResult, Node, Tree } from "components/objectTree/types";
import { ensureExpandedTo, getParentPaths } from "components/objectTree/utils";
import { telemetry } from "telemetry";
import { NodeType } from "types/misc";
import { getFilePathFromObjectPath, getObjectNameFromPath, isLeafNode, isPdfOrDrawingObject } from "utils/objectData";
import { iterateAsync } from "utils/search";

import { ModelTreeFilter, ModelTreeFilterObjects, ModelTreeFilterType, ModelTreeNodeData } from "./types";

/**
 * Natural, numeric-aware ordering for sibling paths so that objects of the same name
 * disambiguated by an index (e.g. "Beam (2)" and "Beam (10)") sort by their index value
 * rather than lexicographically (which would order "(10)" before "(2)").
 */
const childPathCollator = new Intl.Collator(undefined, { numeric: true });

export function expandNode({
    tree,
    path,
    db,
    abortSignal,
    fileTypes,
    skipPdfFiles,
    knownPdfObjectIds,
}: {
    tree: Tree<ModelTreeNodeData>;
    path: string;
    db: ObjectDB;
    abortSignal: AbortSignal;
    fileTypes?: ModelTreeFilterObjects[];
    skipPdfFiles?: boolean;
    knownPdfObjectIds?: Pick<Set<ObjectId>, "has">;
}): AsyncTreeChangeResult<ModelTreeNodeData> {
    const node = tree[path];
    if (!node || node.expanded || node.isLeaf) {
        return { tree };
    }

    if (node.children) {
        // if already loaded - just expand
        const newTree: Tree<ModelTreeNodeData> = { ...tree, [path]: { ...node, expanded: true } };
        return { tree: newTree };
    }

    return {
        tree: { ...tree, [path]: { ...node, loading: true } },
        rollback: (tree) => ({ ...tree, [path]: { ...node, loading: false } }),
        load: async () => {
            const { descendants, iterator } = await loadChildren({
                db,
                path,
                tree,
                fileTypes: fileTypes,
                abortSignal,
                skipPdfFiles,
                knownPdfObjectIds,
            });

            return (tree, relevant) => {
                const node = tree[path];
                if (!node) {
                    return tree;
                }

                const newTree = { ...tree };
                const newNode = appendDescendants({ tree: newTree, node, descendants, iterator });
                newNode.loading = false;
                if (relevant) {
                    newNode.expanded = true;
                }
                newTree[path] = newNode;

                return newTree;
            };
        },
    };
}

export function makeNode(obj: HierarcicalObjectReference): Node<ModelTreeNodeData> {
    const name = getObjectNameFromPath(obj.path);

    const node: Node<ModelTreeNodeData> = {
        expanded: false,
        isLeaf: isLeafNode(obj),
        name,
        path: obj.path,
        data: { object: obj },
        loading: false,
    };

    if (node.isLeaf) {
        const m = name.match(/^(.+)\s+\(?\d+\)?$/);
        if (m) {
            node.groupKey = m[1];
            node.groupCollapsed ??= true;
        }
    }

    return node;
}

export function collapseNode({ tree, path }: { tree: Tree<ModelTreeNodeData>; path: string }) {
    const node = tree[path];
    if (!node) {
        return tree;
    }

    return { ...tree, [path]: { ...node, expanded: false } };
}

export function selectNode({
    tree,
    mainObject,
    db,
    abortSignal,
    fileTypes,
    skipPdfFiles,
    knownPdfObjectIds,
}: {
    tree: Tree<ModelTreeNodeData>;
    mainObject: HierarcicalObjectReference;
    db: ObjectDB;
    abortSignal: AbortSignal;
    fileTypes?: ModelTreeFilterObjects[];
    skipPdfFiles?: boolean;
    knownPdfObjectIds?: Pick<Set<ObjectId>, "has">;
}): {
    /**
     * Either fully updated tree if no async operations needed, or tree with `loading` flag set.
     */
    tree: Tree<ModelTreeNodeData>;
    rollback?: (tree: Tree<ModelTreeNodeData>) => Tree<ModelTreeNodeData>;
    load?: () => Promise<{
        apply: (tree: Tree<ModelTreeNodeData>, relevant: boolean) => Tree<ModelTreeNodeData>;
        loadUpTo?: { parentPath: string; loadUpToPath: string }[];
    }>;
} {
    // if already in the tree - just expand all parents
    if (tree[mainObject.path]) {
        return { tree: ensureExpandedTo(tree, mainObject.path) };
    }

    // if filtered - check that this object can be visible at all
    if (fileTypes) {
        const satisfiesFilter = fileTypes.some(({ objects }) =>
            objects.some((obj) => mainObject.path.startsWith(obj.path) || obj.path.startsWith(mainObject.path)),
        );
        if (!satisfiesFilter) {
            return { tree: tree };
        }
    }

    const parentPaths = [...getParentPaths(mainObject.path)];
    const newTree = { ...tree };

    for (const path of parentPaths) {
        const node = newTree[path];
        if (node && !node.expanded) {
            newTree[path] = node.children ? { ...node, expanded: true } : { ...node, loading: true };
        }
    }

    const loadCriticalPath = async () => {
        const alreadyLoaded = parentPaths.every((path) => newTree[path]);
        if (alreadyLoaded) {
            return parentPaths.map((path) => newTree[path].data.object);
        }

        // The expected ancestor chain is known upfront from the path string (root-first),
        // so collect the first record per expected ancestor instead of taking a fixed
        // number of raw records. A fixed budget can run out before covering every
        // ancestor — duplicate-path records (common in IFC/RVT exports) repeat the same
        // ancestors, and paths deeper than the budget have more distinct ancestors than
        // records taken. A missing ancestor makes apply() stitch nodes across the gap,
        // hoisting a subtree under the wrong parent so it renders in two places.
        // Keeping only the first record per path also dedupes: consecutive critical-path
        // entries sharing a path made apply() insert a node as its own child — a cycle
        // that turned every traversal into an infinite loop and OOM-crashed the renderer.
        const missing = new Set(parentPaths.filter((path) => path !== ""));
        const recordByPath = new Map<string, HierarcicalObjectReference>();

        const iterator = db.search(
            { descentDepth: -(parentPaths.length + 1), parentPath: mainObject.path },
            abortSignal,
        );
        // Cap on raw records scanned (not distinct ancestors), purely as a safety net
        // against a pathological stream; aligned with loadChildren's maxScan.
        const maxScan = 5000;
        for (let scanned = 0; scanned < maxScan && missing.size > 0 && !abortSignal.aborted; scanned++) {
            const next = await iterator.next();
            if (next.done) {
                break;
            }
            if (missing.delete(next.value.path)) {
                recordByPath.set(next.value.path, next.value);
            }
        }

        // Root is synthesized (the search never returns it). Ancestors the search did not
        // return (permission filtering, scan cap) are skipped rather than guessed:
        // appendDescendants refuses non-direct children, and the loadUpTo fallback
        // surfaces the selection through normal child loading instead.
        const criticalPath: HierarcicalObjectReference[] = [tree[""].data.object];
        for (const path of parentPaths) {
            const record = recordByPath.get(path);
            if (record) {
                criticalPath.push(record);
            }
        }
        return criticalPath;
    };

    const load = async () => {
        const loadUpToCandidates: { parentPath: string; loadUpToPath: string }[] = [];
        const [criticalPath, descendantLists] = await Promise.all([
            loadCriticalPath(),
            pMap(
                parentPaths,
                async (parentPath, i) => {
                    const parent = tree[parentPath];
                    const childPath = i === parentPaths.length - 1 ? mainObject.path : parentPaths[i + 1];
                    const { descendants, iterator } = parent?.children
                        ? { descendants: [], iterator: parent.data.childIterator }
                        : await loadChildren({
                              db,
                              path: parentPath,
                              tree,
                              fileTypes: fileTypes,
                              abortSignal,
                              skipPdfFiles,
                              knownPdfObjectIds,
                          });

                    if (!parent?.children?.includes(childPath) && !descendants.some((d) => d.path === childPath)) {
                        loadUpToCandidates.push({
                            parentPath: parentPath,
                            loadUpToPath: childPath,
                        });
                    }

                    return { path: parentPath, descendants, iterator };
                },
                { concurrency: 4, signal: abortSignal },
            ),
        ]);

        const apply = (tree: Tree<ModelTreeNodeData>, relevant: boolean) => {
            const newTree: Tree<ModelTreeNodeData> = { ...tree };
            criticalPath.forEach((obj, pathIndex) => {
                let node = newTree[obj.path];
                if (!node) {
                    node = {
                        expanded: true,
                        name: getObjectNameFromPath(obj.path),
                        path: obj.path,
                        isLeaf: isLeafNode(obj),
                        data: { object: obj },
                        loading: false,
                    };
                } else {
                    node = { ...node };
                }
                if (relevant) {
                    node.expanded = true;
                    node.groupCollapsed = false;
                }
                node.loading = false;
                const { descendants, iterator } = descendantLists.find((d) => d.path === obj.path) ?? {
                    descendants: [] as HierarcicalObjectReference[],
                    iterator: node.data.childIterator,
                };

                // If critical path child is still not loaded - pick it from the critical path.
                const childCriticalPathNode =
                    pathIndex === criticalPath.length - 1 ? mainObject : criticalPath[pathIndex + 1];
                if (
                    !descendants.some((d) => d.path === childCriticalPathNode.path) &&
                    !node.children?.includes(childCriticalPathNode.path)
                ) {
                    descendants.push(childCriticalPathNode);
                }

                if (descendants.length > 0) {
                    node = appendDescendants({
                        tree: newTree,
                        node,
                        descendants: descendants,
                        iterator: iterator,
                    });
                }

                newTree[obj.path] = node;
            });

            // Ensure leaf is not groupCollapsed
            const mainObjectNode = newTree[mainObject.path];
            if (mainObjectNode && mainObjectNode.groupCollapsed) {
                newTree[mainObject.path] = { ...mainObjectNode, groupCollapsed: false };
            }

            return newTree;
        };

        return { apply, loadUpTo: loadUpToCandidates };
    };

    return {
        tree: newTree,
        rollback: (tree) => {
            let newTree = tree;
            for (const path of parentPaths) {
                const node = newTree[path];
                if (node && node.loading) {
                    if (newTree === tree) {
                        newTree = { ...tree };
                    }
                    newTree[path] = { ...node, loading: false };
                }
            }
            return newTree;
        },
        load,
    };
}

export function filterOutObjectsNotOnPath(tree: Tree<ModelTreeNodeData>, paths: string[]) {
    const pathSet = new Set<string>();
    for (const concretePath of paths) {
        for (const path of getParentPaths(concretePath, false)) {
            pathSet.add(path);
        }
    }

    const newTree: Tree<ModelTreeNodeData> = {};
    Object.values(tree).forEach((node) => {
        if (pathSet.has(node.path)) {
            newTree[node.path] = node;
        }
    });

    Object.values(newTree).forEach((node) => {
        if (node.children) {
            node = { ...node, children: node.children.filter((child) => newTree[child]) };
            newTree[node.path] = node;
        }
    });

    return newTree;
}

export async function loadUpTo({
    db,
    tree,
    path,
    loadUpToPath,
    fileTypes,
    abortSignal,
    skipPdfFiles,
    knownPdfObjectIds,
}: {
    db: ObjectDB;
    tree: Tree<ModelTreeNodeData>;
    path: string;
    loadUpToPath: string;
    fileTypes?: ModelTreeFilterObjects[];
    abortSignal: AbortSignal;
    skipPdfFiles?: boolean;
    knownPdfObjectIds?: Pick<Set<ObjectId>, "has">;
}) {
    const { descendants, iterator } = await loadChildren({
        db,
        path,
        tree,
        fileTypes: fileTypes,
        abortSignal,
        ensurePathFound: loadUpToPath,
        skipPdfFiles,
        knownPdfObjectIds,
    });

    return (tree: Tree<ModelTreeNodeData>) => {
        if (descendants.length === 0) {
            return tree;
        }

        const node = tree[path];
        if (!node) {
            return tree;
        }

        const newTree = { ...tree };
        newTree[path] = appendDescendants({ tree: newTree, node: newTree[path], descendants, iterator });
        return newTree;
    };
}

export async function loadFilesWithType({
    db,
    abortSignal,
    fileTypes,
}: {
    db: ObjectDB;
    abortSignal: AbortSignal;
    fileTypes: ModelTreeFilter[];
}) {
    const hasTerrain = fileTypes.some((f) => f.type === ModelTreeFilterType.Terrain);
    const parserTypes = fileTypes.filter((f) => f.type !== ModelTreeFilterType.Terrain).map((f) => f.parserType);

    const [[terrainObjects], [genericTypeObjects]] = await Promise.all([
        hasTerrain
            ? iterateAsync({
                  iterator: db.search(
                      {
                          searchPattern: [
                              {
                                  property: "id",
                                  value: Array.from({ length: 100 }, (_, i) => i.toString()),
                                  exact: true,
                              },
                          ],
                          full: true,
                      },
                      abortSignal,
                  ),
                  count: 100,
                  abortSignal: abortSignal,
              })
            : [[]],
        parserTypes.length
            ? iterateAsync({
                  iterator: db.search(
                      { searchPattern: [{ property: "Parser/Type", value: parserTypes, exact: true }], full: true },
                      abortSignal,
                  ),
                  count: 3000,
                  abortSignal: abortSignal,
              })
            : [[]],
    ]);
    const objectsByFileType = new Map<string, HierarcicalObjectReference[]>();

    if (hasTerrain) {
        objectsByFileType.set(filterTypeToStr({ type: ModelTreeFilterType.Terrain }), terrainObjects);
    }

    for (const obj of genericTypeObjects) {
        const fileName = getFilePathFromObjectPath(obj.path);
        if (!fileName) {
            continue;
        }
        const parserType = (await obj.loadMetaData()).properties.find((p) => p[0] === "Parser/Type")?.[1];
        if (!parserType) {
            continue;
        }
        const key = filterTypeToStr({ type: ModelTreeFilterType.ParserType, parserType });
        let parserTypeObjects = objectsByFileType.get(key);
        if (!parserTypeObjects) {
            parserTypeObjects = [];
            objectsByFileType.set(key, parserTypeObjects);
        }
        parserTypeObjects.push(obj);
    }
    return objectsByFileType;
}

export function* getDescendantsFilteredByParserTypeInfo(path: string, fileTypeFilter: ModelTreeFilterObjects[]) {
    for (const { objects } of fileTypeFilter) {
        for (const obj of objects) {
            if (obj.path.startsWith(path)) {
                const nextSlash = obj.path.indexOf("/", path.length + 1);
                yield nextSlash !== -1 ? obj.path.slice(0, nextSlash) : obj.path;
            }
        }
    }
}

export function collapseAllBelowFileLevel(tree: Tree<ModelTreeNodeData>) {
    const treeMap = { ...tree };
    for (const node of Object.values(treeMap)) {
        const filePath = getFilePathFromObjectPath(node.path);
        if (filePath && filePath.length <= node.path.length && node.expanded) {
            treeMap[node.path] = { ...node, expanded: false };
        }
    }
    return treeMap;
}

export async function getObjectParentFile({
    db,
    mainObject,
    fileTypes,
    tree,
    abortSignal,
}: {
    db: ObjectDB;
    mainObject: HierarcicalObjectReference;
    fileTypes?: ModelTreeFilterObjects[];
    tree?: Tree<ModelTreeNodeData>;
    abortSignal: AbortSignal;
}) {
    if (fileTypes) {
        for (const { objects } of fileTypes) {
            for (const obj of objects) {
                if (mainObject.path.startsWith(obj.path)) {
                    return obj;
                }
            }
        }
    }

    const filePath = getFilePathFromObjectPath(mainObject.path);
    if (!filePath) {
        return;
    }

    if (tree) {
        const node = tree[filePath];
        if (node) {
            return node.data.object;
        }
    }

    for await (const obj of db.search({ parentPath: filePath, descentDepth: 0, full: true }, abortSignal)) {
        return obj;
    }
}

export function loadMore({
    db,
    tree,
    path,
    fileTypes,
    abortSignal,
    skipPdfFiles,
    knownPdfObjectIds,
}: {
    db: ObjectDB;
    tree: Tree<ModelTreeNodeData>;
    path: string;
    fileTypes?: ModelTreeFilterObjects[];
    abortSignal: AbortSignal;
    skipPdfFiles?: boolean;
    knownPdfObjectIds?: Pick<Set<ObjectId>, "has">;
}): AsyncTreeChangeResult<ModelTreeNodeData> {
    const node = tree[path];
    if (!node || !node.hasMoreChildren || !node.children || node.loading) {
        return { tree };
    }

    return {
        tree: { ...tree, [path]: { ...node, loading: true } },
        rollback: (tree) => ({ ...tree, [path]: { ...node, loading: false } }),
        load: async () => {
            const { descendants, iterator } = await loadChildren({
                db,
                path,
                tree,
                fileTypes,
                abortSignal,
                skipPdfFiles,
                knownPdfObjectIds,
            });

            return (tree, _relevant) => {
                const node = tree[path];
                if (!node) {
                    return tree;
                }

                const newNode = appendDescendants({ tree, node, descendants, iterator });
                newNode.loading = false;
                return { ...tree, [path]: newNode };
            };
        },
    };
}

function appendDescendants({
    tree,
    node,
    descendants,
    iterator,
}: {
    tree: Tree<ModelTreeNodeData>;
    node: Node<ModelTreeNodeData>;
    descendants: HierarcicalObjectReference[];
    iterator?: AsyncIterableIterator<HierarcicalObjectReference>;
}) {
    for (const child of descendants) {
        let node = tree[child.path];
        if (!node) {
            node = makeNode(child);
            tree[child.path] = node;
        }
    }

    // Dedupe by path against BOTH the existing children and the incoming batch itself. Some
    // models (e.g. certain IFC exports) contain several sibling records with the exact same
    // path; the tree is keyed by path, so they all map to one node. Letting the same path
    // into `children` more than once makes every tree traversal (makeNodeList, counters,
    // descendant collection) expand that shared subtree once per duplicate — exponential
    // (3^depth) when duplicates nest, which exhausts renderer memory and crashes the tab.
    // A node must never list itself as a child: that creates a cycle which turns every
    // traversal into an infinite loop (renderer OOM).
    const childPaths = new Set(node.children ?? []);
    const newChildPaths: string[] = [];
    // A child must be a DIRECT child of this node: its path extends the node's path by
    // exactly one segment. Anything else (the node itself, a sibling, a grandchild) means
    // an upstream chain was built across a gap — attaching it here would render the
    // subtree in a second, wrong location (and self-references loop traversals forever).
    const expectedParent = node.path ? `${node.path}/` : "";
    let rejectedCount = 0;
    for (const d of descendants) {
        const isDirectChild =
            d.path.length > expectedParent.length &&
            d.path.startsWith(expectedParent) &&
            !d.path.includes("/", expectedParent.length);
        if (!isDirectChild) {
            rejectedCount++;
            continue;
        }

        if (!childPaths.has(d.path)) {
            childPaths.add(d.path);
            newChildPaths.push(d.path);
        }
    }

    if (rejectedCount > 0) {
        // A rejection means some caller tried to attach a non-child — expected only when
        // the critical-path search came back with gaps (permission filtering, scan cap).
        // Surface it so bad chains are diagnosable instead of silently absorbed.
        telemetry.trackEvent("model_tree.non_direct_child_rejected", {
            "tree.parent_path_depth": node.path === "" ? 0 : node.path.split("/").length,
            "tree.rejected_count": rejectedCount,
            "tree.batch_size": descendants.length,
        });
    }

    return {
        ...node,
        children: [...(node.children ?? []), ...newChildPaths].sort((a, b) => childPathCollator.compare(a, b)),
        hasMoreChildren: Boolean(iterator),
        data: { ...node.data, childIterator: iterator },
    };
}

async function loadChildren({
    db,
    path,
    tree,
    fileTypes,
    abortSignal,
    ensurePathFound,
    skipPdfFiles,
    knownPdfObjectIds,
}: {
    db: ObjectDB;
    tree: Tree<ModelTreeNodeData>;
    path: string;
    fileTypes?: ModelTreeFilterObjects[];
    abortSignal: AbortSignal;
    /**
     * By default, we load first 500 children and the rest is loaded lazily.
     * But if we need to ensure some object (e.g. selected object) is loaded - we need to keep loading children
     * until we find that object.
     */
    ensurePathFound?: string;
    skipPdfFiles?: boolean;
    knownPdfObjectIds?: Pick<Set<ObjectId>, "has">;
}) {
    const isFileOrInsideFile = getFilePathFromObjectPath(path);

    const pathFilter =
        fileTypes && !isFileOrInsideFile ? [...new Set(getDescendantsFilteredByParserTypeInfo(path, fileTypes))] : [];

    const node: Node<ModelTreeNodeData> | undefined = tree[path];
    const iterator =
        node?.data.childIterator ??
        db.search(
            {
                parentPath: path,
                descentDepth: 1,
                searchPattern: pathFilter.length ? [{ property: "path", value: pathFilter, exact: true }] : [],
            },
            abortSignal,
        );
    const descendants: HierarcicalObjectReference[] = [];
    let done = false;
    let scanned = 0;
    const maxScan = 5000;
    // Budget the page by *distinct, not-yet-shown* child paths rather than raw records. The tree
    // is keyed by path, so duplicate-path records (common in some IFC/DWG exports) collapse to a
    // single row. Counting raw records here let a whole page dedupe down to nothing, so
    // "Load more" issued a search but added no visible rows and the button never cleared. Seeding
    // from the already-shown children means each call resumes past what the user can already see.
    const seenPaths = new Set<string>(node?.children ?? []);
    // Even if object is in the list of children - we consider it's not yet found.
    // It could be loaded individually, so by using ensurePathFound we want to load all the objects above it.
    let objectFound = false;
    while (descendants.length < 500 || (ensurePathFound && !objectFound)) {
        if (abortSignal.aborted) {
            break;
        }

        // Cap every scan, including selection-driven expansion. A selected child is injected
        // into the critical path separately by selectNode(), so continuing through an
        // unbounded sibling list merely to find it can retain millions of references and crash
        // the renderer on models with duplicated hierarchy paths.
        if (scanned >= maxScan) {
            break;
        }

        const next = await iterator.next();
        if (next.done) {
            done = true;
            break;
        }

        scanned++;
        const child = next.value;
        if (skipPdfFiles && !isFileOrInsideFile && (await isPdfOrDrawingObject(child, { knownPdfObjectIds }))) {
            // If the filtered-out child is the ensurePathFound target,
            // treat it as found so the loop doesn't scan unboundedly for
            // a target that can never be appended.
            if (ensurePathFound && !objectFound && ensurePathFound === child.path) {
                objectFound = true;
            }
            continue;
        }

        if (ensurePathFound && !objectFound && ensurePathFound === child.path) {
            objectFound = true;
        }

        // Skip paths already shown (or already seen earlier in this same scan) so the page fills
        // with genuinely new rows instead of collapsing to nothing in appendDescendants.
        if (seenPaths.has(child.path)) {
            continue;
        }
        seenPaths.add(child.path);
        descendants.push(child);
    }

    return { descendants, iterator: done ? undefined : iterator };
}

export const emptyModelTree: Tree<ModelTreeNodeData> = {
    "": {
        expanded: false,
        name: "Root", // Root name is never displayed, just for debugging
        isLeaf: false,
        path: "",
        loading: true,
        data: (() => {
            const object = {
                id: -1,
                path: "",
                name: "Root",
                properties: [],
                save: async () => false,
                type: NodeType.Internal,
                loadMetaData: async () => object,
            };
            return { object };
        })(),
    },
};

export function filterTypeToStr(filter: ModelTreeFilter) {
    switch (filter.type) {
        case ModelTreeFilterType.Terrain:
            return "terrain";
        case ModelTreeFilterType.ParserType:
            return `parser:${filter.parserType}`;
        default:
            throw new Error("Unknown filter type");
    }
}
